import {
  getImpersonatedClientIdFromUser,
  getImpersonatedCompanyIdFromUser,
  getSessionClientId,
  isSessionClientId,
  isSuperAdminSession,
  isUserImpersonating,
} from 'src/utils/authImpersonation';

export interface UserRowLookup {
  rowId: string;
  ancestorRowIds: string[];
}

export interface UserRowNode {
  id: string;
  realClientId: number;
  subUsers?: UserRowNode[];
}

export interface CompanyRowNode {
  id: number;
  companies?: CompanyRowNode[];
}

export interface CompanyRowLookup {
  companyId: number;
  ancestorIds: number[];
}

export const findCompanyInTree = (
  companies: CompanyRowNode[],
  companyId: number,
  ancestorIds: number[] = []
): CompanyRowLookup | null => {
  for (const company of companies) {
    if (company.id === companyId) {
      return { companyId: company.id, ancestorIds };
    }
    if (company.companies?.length) {
      const found = findCompanyInTree(company.companies, companyId, [...ancestorIds, company.id]);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

export const resolveDefaultCompanyId = (
  companies: CompanyRowNode[],
  preferredCompanyId: number | null
): number | null => {
  if (preferredCompanyId == null) {
    return null;
  }
  return findCompanyInTree(companies, preferredCompanyId)?.companyId ?? null;
};

export const isWorkAreaImpersonating = isUserImpersonating;
export const getImpersonatedCompanyId = getImpersonatedCompanyIdFromUser;
export const getImpersonatedClientId = getImpersonatedClientIdFromUser;

export const isSuperAdminWorkAreaSession = isSuperAdminSession;

/**
 * Lock (make non-selectable) the company tree root for a normal user when the
 * root node is one of the known super-admin companies.  Super Admin sessions
 * always skip the root node entirely, so locking is only relevant for
 * non-super-admin sessions.
 */
export const hasLockedSuperAdminRootForNonSuperAdmin = (
  rootCompanyId: number | null | undefined,
  isSuperAdminSession: boolean,
  superAdminCompanyIds: number[]
): boolean => {
  if (isSuperAdminSession || rootCompanyId == null) return false;
  return superAdminCompanyIds.includes(rootCompanyId);
};

export const isImpersonatedClient = (
  clientId: number | null | undefined,
  user: Parameters<typeof isUserImpersonating>[0]
): boolean => isSessionClientId(clientId, getSessionClientId(user?.ClientId));

/** Scrolls the work-area panel body when the target row is in the DOM; returns false if not ready yet. */
export const scrollWorkAreaPanelToSelector = (
  container: HTMLElement | null | undefined,
  selector: string
): boolean => {
  const element = container?.querySelector(selector);
  if (!(element instanceof Element)) {
    return false;
  }
  if (typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ block: 'nearest' });
  }
  return true;
};

export const findUserRowByClientId = (
  users: UserRowNode[],
  clientId: number,
  ancestorRowIds: string[] = []
): UserRowLookup | null => {
  for (const user of users) {
    if (user.realClientId === clientId) {
      return { rowId: user.id, ancestorRowIds };
    }
    if (user.subUsers?.length) {
      const found = findUserRowByClientId(user.subUsers, clientId, [...ancestorRowIds, user.id]);
      if (found) {
        return found;
      }
    }
  }
  return null;
};
